import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  CreditCard as ViewIcon,
  CreditCard as TerminalIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Loader } from '@googlemaps/js-api-loader';
import { db, storage } from '../../../firebase/config';
import AddTerminalModal from '../modals/AddTerminalModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Terminal,
  TerminalStatus,
  mapLegacyDeviceToTerminal,
  getTerminalStatusColor,
  getTerminalStatusLabel
} from '../../../types/terminal';

// Declare global google object
declare global {
  interface Window {
    google: typeof google;
  }
}

/* Site card — Interactive High-Blur Glass (same as ReportCard) */
const SiteCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  marginBottom: '16px',
  background: 'rgba(255, 255, 255, 0.03)',
  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(34, 211, 238, 0.3)',
    transform: 'translateY(-5px)',
    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const SiteImage = styled(CardMedia)(({ theme }) => ({
  height: 120,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
}));

/* Edit/Delete — liquid glass pill (same as Reports) */
const ActionButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  borderRadius: '50px',
  margin: '0 4px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    background: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
  },
}));

/* Add Site — pill style (same as Reports Filter/primary buttons) */
const AddSiteButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#22d3ee',
  border: '1px solid rgba(34, 211, 238, 0.5)',
  boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.1)',
  padding: '8px 20px',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 'bold',
  textTransform: 'none',
  fontSize: '0.8125rem',
  borderRadius: '50px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(34, 211, 238, 0.25)',
    borderColor: '#22d3ee',
    boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

/* Terminal card — Interactive High-Blur Glass (subtle cyan) */
const TerminalCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  marginBottom: '8px',
  padding: '12px 16px',
  background: 'rgba(255, 255, 255, 0.03)',
  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.04) 0%, transparent 100%)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderLeft: '1px solid rgba(34, 211, 238, 0.15)',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(34, 211, 238, 0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px -12px rgba(0, 0, 0, 0.5), inset 0 0 16px rgba(34, 211, 238, 0.08)',
  },
}));

const StatusChip = styled(Chip)<{ terminalstatus: TerminalStatus }>(({ terminalstatus, theme }) => ({
  backgroundColor: getTerminalStatusColor(terminalstatus),
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  height: '20px',
  fontSize: '0.7rem',
}));

interface Site {
  id: string;
  name: string;
  address: string;
  photo?: string;
  masterOwnerID: string;
  createdAt: any;
  devices?: any[];
  terminals?: Terminal[];
  description?: string;
  contactPerson?: string;
  contactPhone?: string;
}

interface AddSiteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (siteData: any) => Promise<void>;
  editingSite?: Site | null;
}

const AddSiteModal: React.FC<AddSiteModalProps> = ({ open, onClose, onSave, editingSite }) => {
  const { colors } = useTheme();
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    contactPerson: '',
    contactPhone: '',
    photo: null as File | null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.formatted_address && !place.name) return;

    // Use the formatted address from Google Places
    const fullAddress = place.formatted_address || place.name || '';

    setFormData(prev => ({
      ...prev,
      address: fullAddress,
    }));
  };

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places'],
        });

        await loader.load();
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    loadGoogleMaps();
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (isGoogleMapsLoaded && autocompleteInputRef.current && !autocompleteRef.current && window.google) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'us' },
        }
      );

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    }
  }, [isGoogleMapsLoaded]);

  useEffect(() => {
    if (editingSite) {
      setFormData({
        name: editingSite.name || '',
        address: editingSite.address || '',
        description: editingSite.description || '',
        contactPerson: editingSite.contactPerson || '',
        contactPhone: editingSite.contactPhone || '',
        photo: null,
      });
    } else {
      setFormData({
        name: '',
        address: '',
        description: '',
        contactPerson: '',
        contactPhone: '',
        photo: null,
      });
    }
  }, [editingSite]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      address: e.target.value,
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, photo: file }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address) {
      setError('Please fill in required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let photoURL = editingSite?.photo || null;

      // Upload photo if provided
      if (formData.photo) {
        const photoRef = ref(storage, `sites/${Date.now()}_${formData.photo.name}`);
        await uploadBytes(photoRef, formData.photo);
        photoURL = await getDownloadURL(photoRef);
      }

      const siteData = {
        ...formData,
        photo: photoURL,
        updatedAt: new Date(),
      };

      await onSave(siteData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save site');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Inter', sans-serif",
          color: '#ffffff',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
          backgroundColor: 'transparent',
        }}
      >
        {editingSite ? 'Edit Site' : 'Add New Site'}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}

        <Typography
          component="span"
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            color: '#22d3ee',
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            display: 'block',
            marginBottom: 2,
          }}
        >
          Site details
        </Typography>

        <Box sx={{ marginTop: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Site Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) => handleInputChange('contactPerson', e.target.value)}
              disabled={loading}
            />
          </Box>

          <TextField
            fullWidth
            label="Address *"
            value={formData.address}
            onChange={handleAddressChange}
            disabled={loading}
            placeholder="Start typing your address..."
            inputRef={autocompleteInputRef}
            helperText={isGoogleMapsLoaded ? "Start typing to see address suggestions" : "Loading address suggestions..."}
            sx={{ marginBottom: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e.target.value)}
              disabled={loading}
            />

            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={loading}
              sx={{
                height: '56px',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(16px)',
                borderRadius: '50px',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderColor: 'rgba(34, 211, 238, 0.4)',
                },
              }}
            >
              {formData.photo ? formData.photo.name : 'Upload Site Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            disabled={loading}
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderColor: 'rgba(255, 255, 255, 0.25)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            backdropFilter: 'blur(16px)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.1)',
            '&:hover': {
              background: 'rgba(34, 211, 238, 0.25)',
              borderColor: '#22d3ee',
              boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.15)',
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              color: colors.TextMuted,
              background: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          {loading ? 'Saving...' : 'Save Site'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SitesTab: React.FC = () => {
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);
  const [isAddTerminalModalOpen, setIsAddTerminalModalOpen] = useState(false);
  const [selectedSiteForTerminal, setSelectedSiteForTerminal] = useState<Site | null>(null);

  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const sectionHeading = 'Site Management';
  const canAddSites = userProfile?.role === 'Admin';

  const fetchSites = async () => {
    try {
      setLoading(true);
      const sitesCollection = collection(db, 'sites');
      const q = query(
        sitesCollection,
        where('masterOwnerID', '==', userProfile?.masterOwnerID)
      );
      const sitesSnapshot = await getDocs(q);
      const sitesData = await Promise.all(
        sitesSnapshot.docs.map(async (siteDoc) => {
          const siteData = { id: siteDoc.id, ...siteDoc.data() } as Site;

          // Fetch terminals for this site
          try {
            const devicesCollection = collection(db, `sites/${siteDoc.id}/devices`);
            const devicesSnapshot = await getDocs(devicesCollection);
            const terminals = devicesSnapshot.docs.map(deviceDoc => {
              const deviceData = { id: deviceDoc.id, ...deviceDoc.data() };
              return mapLegacyDeviceToTerminal(deviceData);
            });

            siteData.terminals = terminals;
          } catch (err) {
            console.error(`Error fetching terminals for site ${siteDoc.id}:`, err);
            siteData.terminals = [];
          }

          return siteData;
        })
      );

      // Filter by assigned sites for non-admin users
      if (userProfile?.role === 'Clerk' && userProfile.assignedSites) {
        const filteredSites = sitesData.filter(site =>
          userProfile.assignedSites?.includes(site.id)
        );
        setSites(filteredSites);
      } else {
        setSites(sitesData);
      }
    } catch (err: any) {
      setError('Failed to fetch sites');
      console.error('Error fetching sites:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.masterOwnerID) {
      fetchSites();
    }
  }, [userProfile]);

  const handleAddSite = async (siteData: any) => {
    const newSite = {
      ...siteData,
      masterOwnerID: userProfile?.masterOwnerID,
      createdAt: new Date(),
      id: `site_${Date.now()}`,
    };

    const siteRef = doc(db, 'sites', newSite.id);
    await setDoc(siteRef, newSite);

    setSites(prev => [...prev, newSite]);
  };

  const handleEditSite = async (siteData: any) => {
    if (!editingSite) return;

    // Helper function to recursively remove undefined values
    const removeUndefinedValues = (obj: any): any => {
      if (obj === null || obj === undefined) return null;
      if (typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.filter(item => item !== undefined).map(removeUndefinedValues);

      const cleaned: any = {};
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value !== undefined) {
          cleaned[key] = removeUndefinedValues(value);
        }
      });
      return cleaned;
    };

    const updatedSite = {
      ...editingSite,
      ...siteData,
      updatedAt: new Date(),
    };

    // Remove undefined values before sending to Firestore
    const cleanedSiteData = removeUndefinedValues(updatedSite);

    const siteRef = doc(db, 'sites', editingSite.id);
    await updateDoc(siteRef, cleanedSiteData);

    setSites(prev => prev.map(site =>
      site.id === editingSite.id ? updatedSite : site
    ));
    setEditingSite(null);
  };

  const handleDeleteSite = async () => {
    if (!siteToDelete) return;

    try {
      const siteRef = doc(db, 'sites', siteToDelete.id);
      await deleteDoc(siteRef);

      setSites(prev => prev.filter(site => site.id !== siteToDelete.id));
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    } catch (err: any) {
      setError('Failed to delete site');
    }
  };

  const openEditModal = (site: Site) => {
    setEditingSite(site);
    setAddModalOpen(true);
  };

  const closeModal = () => {
    setAddModalOpen(false);
    setEditingSite(null);
  };

  const openDeleteDialog = (site: Site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const openAddTerminalModal = (site: Site) => {
    setSelectedSiteForTerminal(site);
    setIsAddTerminalModalOpen(true);
  };

  const renderTerminalCard = (terminal: Terminal, siteId: string) => (
    <TerminalCard
      key={terminal.id}
      onClick={() => navigate(`/sites/${siteId}`)}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" flex={1}>
          <TerminalIcon sx={{ color: colors.TextMuted, fontSize: 16, marginRight: 1 }} />
          <Box>
            <Typography
              variant="body2"
              color={colors.TextPrimary}
              sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}
            >
              {terminal.name}
            </Typography>
            <Typography
              variant="caption"
              color={colors.TextSecondary}
              sx={{ fontSize: '0.7rem' }}
            >
              {terminal.terminalType} • {terminal.makeModel || 'Unknown'}
            </Typography>
          </Box>
        </Box>

        <StatusChip
          label={getTerminalStatusLabel(terminal.status)}
          terminalstatus={terminal.status}
          size="small"
        />
      </Box>

      {terminal.siteLocation?.description && (
        <Typography
          variant="caption"
          color={colors.TextMuted}
          sx={{ fontSize: '0.7rem', marginTop: 0.5, display: 'block' }}
        >
          {terminal.siteLocation.description}
        </Typography>
      )}
    </TerminalCard>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card
        className="card-4"
        sx={{
          marginBottom: 3,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          '&:hover': {
            border: '1px solid rgba(34, 211, 238, 0.4)',
            boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <CardContent sx={{ py: 3, px: 3, '&:last-child': { pb: 3 } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'rgba(34, 211, 238, 0.2)', paddingBottom: 2, marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 700,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
              }}
            >
              {sectionHeading}
            </Typography>
            {canAddSites && (
              <AddSiteButton
                startIcon={<AddIcon />}
                onClick={() => setAddModalOpen(true)}
                sx={{ marginBottom: 0 }}
              >
                Add Site
              </AddSiteButton>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          {sites.length === 0 ? (
            <Box textAlign="center" padding={4}>
              <Typography color={colors.TextSecondary} variant="h6">
                No sites available
              </Typography>
              <Typography color={colors.TextMuted} sx={{ marginTop: 1 }}>
                {userProfile?.role === 'Admin'
                  ? 'Click "Add Site" to create your first site'
                  : 'Contact your administrator to get access to sites'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
              {sites.map((site) => (
                <SiteCard key={site.id}>
              <SiteImage>
                {site.photo ? (
                  <img
                    src={site.photo}
                    alt={site.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      backgroundColor: colors.Gradient,
                      fontSize: '24px',
                    }}
                  >
                    {site.name.charAt(0)}
                  </Avatar>
                )}
              </SiteImage>

              <CardContent sx={{ padding: '16px' }}>
                <Typography
                  variant="h6"
                  color={colors.TextPrimary}
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  {site.name}
                </Typography>

                <Box display="flex" alignItems="flex-start" marginBottom={1}>
                  <LocationIcon
                    sx={{ color: colors.TextMuted, fontSize: 16, marginTop: '2px', marginRight: '8px' }}
                  />
                  <Typography
                    variant="body2"
                    color={colors.TextSecondary}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {site.address}
                  </Typography>
                </Box>

                {site.description && (
                  <Typography
                    variant="body2"
                    color={colors.TextMuted}
                    sx={{ marginBottom: 2, fontStyle: 'italic' }}
                  >
                    {site.description}
                  </Typography>
                )}

                {/* Terminals Section */}
                <Box marginBottom={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1.5}>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "'JetBrains Mono', monospace",
                        color: '#22d3ee',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: '0.3em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Terminals ({site.terminals?.length || 0})
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {userProfile?.role === 'Admin' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => openAddTerminalModal(site)}
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 'bold',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            borderRadius: '50px',
                            borderColor: 'rgba(34, 211, 238, 0.5)',
                            color: '#22d3ee',
                            backgroundColor: 'transparent',
                            py: 0.5,
                            px: 1.5,
                            '&:hover': {
                              backgroundColor: 'rgba(34, 211, 238, 0.2)',
                              borderColor: '#22d3ee',
                              color: '#ffffff',
                            },
                          }}
                        >
                          Add Terminal
                        </Button>
                      )}
                      {site.terminals && site.terminals.length > 0 && (
                        <Chip
                          label={`${site.terminals.filter(t => t.status === 'clean').length} clean`}
                          size="small"
                          sx={{
                            backgroundColor: getTerminalStatusColor('clean'),
                            color: colors.Light_Shadow,
                            fontSize: '0.7rem',
                            height: '20px',
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {site.terminals && site.terminals.length > 0 ? (
                    <Box sx={{ maxHeight: '200px', overflowY: 'scroll', paddingRight: '12px' }}>
                      {site.terminals.slice(0, 3).map(terminal => renderTerminalCard(terminal, site.id))}
                      {site.terminals.length > 3 && (
                        <Typography
                          variant="caption"
                          color={colors.TextMuted}
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            marginTop: 1,
                            fontStyle: 'italic',
                            cursor: 'pointer',
                            '&:hover': {
                              color: colors.Gradient,
                            }
                          }}
                          onClick={() => navigate(`/sites/${site.id}`)}
                        >
                          +{site.terminals.length - 3} more terminals - Click to view all
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: 'center',
                        padding: 2,
                        backgroundColor: colors.Background,
                        borderRadius: '8px',
                        border: `1px dashed rgba(255, 255, 255, 0.1)`
                      }}
                    >
                      <TerminalIcon sx={{ color: colors.TextMuted, fontSize: 24, marginBottom: 1 }} />
                      <Typography
                        variant="caption"
                        color={colors.TextMuted}
                        sx={{ display: 'block' }}
                      >
                        No terminals assigned
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ marginBottom: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />

                {userProfile?.role === 'Admin' && (
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <ActionButton size="small" onClick={() => openEditModal(site)} title="Edit">
                      <EditIcon />
                    </ActionButton>
                    <ActionButton
                      size="small"
                      onClick={() => openDeleteDialog(site)}
                      title="Delete"
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.Error,
                          color: colors.Light_Shadow,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </ActionButton>
                  </Box>
                )}
              </CardContent>
            </SiteCard>
          ))}
        </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Site Modal */}
      <AddSiteModal
        open={addModalOpen}
        onClose={closeModal}
        onSave={editingSite ? handleEditSite : handleAddSite}
        editingSite={editingSite}
      />

      {/* Add Terminal Modal */}
      <AddTerminalModal
        open={isAddTerminalModalOpen}
        onClose={() => {
          setIsAddTerminalModalOpen(false);
          setSelectedSiteForTerminal(null);
        }}
        site={selectedSiteForTerminal}
        onTerminalAdded={() => {
          fetchSites();
          setIsAddTerminalModalOpen(false);
          setSelectedSiteForTerminal(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Delete Site
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Typography color={colors.TextPrimary}>
            Are you sure you want to delete "{siteToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSite}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(244, 67, 54, 0.5)',
              background: 'rgba(244, 67, 54, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(244, 67, 54, 0.3)', borderColor: 'rgba(244, 67, 54, 0.7)' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SitesTab;
